import { put, select, call, takeEvery, all, takeLatest } from 'redux-saga/effects';
import { Errors } from '@blink/components/src/constants/errors';
import { AbstractWallet } from '@blink/components/src/utils';
import Logger from '@blink/components/src/utils/logger';
import { formatUnits } from 'ethers';

import { getWalletMap } from '@/shared/utils/getWalletMap';
import { Store } from '@/shared/store';
import {
    assetPriceGeneralErrorAction,
    clearErrorsAction,
    collateralAgreementErrorAction,
    collateralGeneralErrorAction,
    collateralTokenErrorAction,
    collateralWalletErrorAction,
    userBorrowsErrorAction,
} from '@/shared/store/errors/actions';
import {
    setUserBorrowsOpenedAction,
    setUserBorrowsClosedAction,
    setUserBorrowsRegisteredAction,
    setUserBorrowsSuspendedAction,
    setCollaterals,
    setPrices,
} from '@/shared/store/borrows/actions';
import {
    GET_COLLATERALS,
    GET_PRICES,
    GET_USER_BORROWS_CLOSED,
    GET_USER_BORROWS_OPENED,
    GET_USER_BORROWS_REGISTERED,
    GET_USER_BORROWS_SUSPENDED,
} from '@/shared/store/borrows/constants';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import {
    Agreement,
    BorrowStatusType,
    getPrice,
    getTokens,
    getUserBorrows,
    Price,
    ROLE,
    Token,
    UserBorrow,
} from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { getSafeSigner } from '@/shared/utils/getSafeSigner';
import {
    ASSET_PRICE_ERROR,
    ASSET_PRICE_GENERAL_ERROR,
    ASSET_PRICES_ERROR,
    COLLATERAL_AGREEMENT_ERROR,
    COLLATERAL_GENERAL_ERROR,
    COLLATERAL_TOKENS_ERROR,
    COLLATERAL_WALLET_ERROR,
    USER_BORROWS_ERROR,
} from '@/shared/store/errors/constants';

function* fetchUserBorrows(
    statusToFetch: BorrowStatusType,
    successAction: (arg0: UserBorrow[]) => any,
) {
    const status = statusToFetch;
    try {
        yield put(clearErrorsAction({ errorType: USER_BORROWS_ERROR }));

        const { walletAddress, agreements } = yield select((state: Store) => ({
            walletAddress: state.wallets.active.address,
            agreements: state.agreements.userBorrows,
        }));

        const userBorrows: UserBorrow[] = yield call(getUserBorrows, {
            walletAddress,
            status,
            withoutCache: true,
        });

        if (!userBorrows || userBorrows.length === 0 || !agreements || agreements.length === 0) {
            yield put(successAction([] as UserBorrow[]));
            return;
        }

        const borrowerAgreements = new Map(
            agreements
                .filter((agreement: Agreement) => agreement.role === ROLE.BORROWER)
                .map((agreement: Agreement) => [agreement.address, agreement.apy]),
        );

        const filteredBorrows = userBorrows
            .map((item) => ({
                ...item,
                apy: borrowerAgreements.get(item.agreementAddress) || 'N/A',
            }))
            .filter((item) => borrowerAgreements.has(item.agreementAddress));

        yield put(successAction(filteredBorrows as UserBorrow[]));
    } catch (e: any) {
        yield put(successAction([] as UserBorrow[]));
        yield put(
            userBorrowsErrorAction({
                message:
                    e?.status === 403 || e?.status === 431
                        ? `${Errors.PERMISSION_DENIED} to get user borrows with status: ${status}`
                        : `Status: ${status}; Error: ${e.message || Errors.USER_BORROWS}  `,
            }),
        );
    }
}

function* getUserBorrowsOpenedSaga() {
    yield call(fetchUserBorrows, BorrowStatusType.OPENED, setUserBorrowsOpenedAction);
}

function* getUserBorrowsSuspendedSaga() {
    yield call(fetchUserBorrows, BorrowStatusType.SUSPENDED, setUserBorrowsSuspendedAction);
}

function* getUserBorrowsRegisteredSaga() {
    yield call(fetchUserBorrows, BorrowStatusType.REGISTERED, setUserBorrowsRegisteredAction);
}

function* getUserBorrowsClosedSaga() {
    yield call(fetchUserBorrows, BorrowStatusType.CLOSED, setUserBorrowsClosedAction);
}

function* getCollateralsSaga(action: { type: string; payload: string }): Generator<any, any, any> {
    const agreementAddress = action.payload;

    try {
        yield put(clearErrorsAction({ errorType: COLLATERAL_AGREEMENT_ERROR }));
        yield put(clearErrorsAction({ errorType: COLLATERAL_WALLET_ERROR }));
        yield put(clearErrorsAction({ errorType: COLLATERAL_TOKENS_ERROR }));
        yield put(clearErrorsAction({ errorType: COLLATERAL_GENERAL_ERROR }));

        const {
            type,
            blockchainName,
            walletAddress,
            agreements,
        }: { type: any; blockchainName: any; walletAddress: string; agreements: Agreement[] } =
            yield select((state: Store) => ({
                type: state.wallets.active.type,
                blockchainName: state.wallets.network,
                walletAddress: state.wallets.active.address,
                agreements: state.agreements.borrowerAgreements,
            }));

        const agreement = agreements.find(
            (agreement) =>
                agreement.address === agreementAddress && agreement.role === ROLE.BORROWER,
        );

        if (!agreement) {
            yield put(collateralAgreementErrorAction({ message: Errors.AGREEMENT_NOT_FOUND }));
            return;
        }

        const tokenAddresses = agreement.collaterals;

        const walletMap = getWalletMap();

        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;

        if (!wallet) {
            yield put(collateralWalletErrorAction({ message: Errors.ADD_WALLETS }));
            return;
        }

        const walletProvider: any = yield wallet.getProvider();
        const signer = yield getSafeSigner(walletProvider, blockchainName);

        const tokens: Token[] = yield call(getTokens);

        const excludedAddress = [
            '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
            '0x0000000000000000000000000000000000000348',
            '0xbBbBBBBbbBBBbbbBbbBbbbbBBbBbbbbBbBbbBBbB',
        ];

        const filteredTokens = tokens.filter(
            (token) => !excludedAddress.includes((token.address || '').trim()),
        );

        const tokensByAddresses = filteredTokens.filter((token) =>
            tokenAddresses.includes(token.address as string),
        );

        const tokenBalances = yield all(
            tokensByAddresses.map((token) =>
                call(function* () {
                    try {
                        const tokenContract = getSafeContract(
                            token.address as string,
                            IERCAbi,
                            signer,
                        );

                        const balance = yield tokenContract.balanceOf(walletAddress);

                        token.balance = formatUnits(balance as string, token.decimals);
                    } catch (e: any) {
                        Logger.error('Error in borrow:tokenContract:', { error: e });

                        yield put(
                            collateralTokenErrorAction({
                                message:
                                    e.message ||
                                    Errors.TOKEN_CONTRACT_NOT_AVAILABLE + ' ' + token.name,
                            }),
                        );
                    }
                    return token;
                }),
            ),
        );

        yield put(setCollaterals(tokenBalances as Token[]));
    } catch (e: any) {
        yield put(setCollaterals([]));

        yield put(collateralGeneralErrorAction({ message: e?.message || Errors.TOKENS }));
        Logger.error('Error in borrow:getCollateralsSaga:', { error: e });
    }
}

function* getAssetPrices(action: {
    type: string;
    payload: { assetsToCollateral: Token[]; leverageAddress: string };
}) {
    try {
        yield put(clearErrorsAction({ errorType: ASSET_PRICES_ERROR }));
        yield put(clearErrorsAction({ errorType: ASSET_PRICE_ERROR }));
        yield put(clearErrorsAction({ errorType: ASSET_PRICE_GENERAL_ERROR }));

        const { assetsToCollateral, leverageAddress } = action.payload;

        const prices: Array<(Token & Price) | null> = yield all(
            assetsToCollateral.map((asset: Token) =>
                call(function* (): Generator<Price, any, any> {
                    try {
                        const response = yield call(
                            getPrice as any,
                            asset.address,
                            leverageAddress,
                        ) as Price;

                        return {
                            ...asset,
                            price: response.price,
                        };
                    } catch (e: any) {
                        Logger.error(`Error in borrow::getPrice:address:${asset.address}`, {
                            error: e,
                        });

                        return null;
                    }
                }),
            ),
        );

        const validPrices = prices.filter((price) => price !== null);

        if (validPrices.length > 0) {
            yield put(setPrices(validPrices));
        } else {
            yield put(assetPriceGeneralErrorAction({ message: Errors.NO_PRICES_AVAILABLE }));
        }
    } catch (e: any) {
        Logger.error('Error in borrow:getAssetPrices saga:', { error: e });

        yield put(
            assetPriceGeneralErrorAction({ message: e.message || Errors.NO_PRICES_AVAILABLE }),
        );
    }
}

export function* borrowsWatcher() {
    yield takeEvery(GET_USER_BORROWS_OPENED, getUserBorrowsOpenedSaga);
    yield takeEvery(GET_USER_BORROWS_SUSPENDED, getUserBorrowsSuspendedSaga);
    yield takeEvery(GET_USER_BORROWS_REGISTERED, getUserBorrowsRegisteredSaga);
    yield takeEvery(GET_USER_BORROWS_CLOSED, getUserBorrowsClosedSaga);
    yield takeLatest(GET_COLLATERALS, getCollateralsSaga);
    yield takeLatest(GET_PRICES, getAssetPrices);
}
